import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UICloseButton from 'UIComponents/button/UICloseButton';
import { H2, H4 } from 'UIComponents/elements/headings';
import UIPanel from 'UIComponents/panel/UIPanel';
import UIPanelHeader from 'UIComponents/panel/UIPanelHeader';
import UIPanelBody from 'UIComponents/panel/UIPanelBody';
import { getActionHeaderTitle } from 'automation-ui-action-features/records/recordsUtils';
import { useGetIsUngated } from 'automation-ui-core/hooks/authHooks';
import UILink from 'UIComponents/link/UILink';
import UIPanelSection from 'UIComponents/panel/UIPanelSection';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { PLATFORM_ACTION_EDIT } from 'automation-ui-canvas/constants/AbsolutePaths';
import ActionConfigBasePaths from 'automation-ui-action-features/actions/ActionConfigBasePaths';
import UIIllustration from 'UIComponents/image/UIIllustration';
import UIFlex from 'UIComponents/layout/UIFlex';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { flowDetailsUrl } from 'automation-ui-routing/workflowsUrls';
import { basePaths } from 'automation-ui-canvas/constants/Routes';
import { ApplicationHistoryFilterKeys } from 'automation-ui-management/constants/ApplicationHistoryFilterKeys';
import { NotificationTypes } from 'automation-ui-log-events/constants/LogEventConstants';

// @ts-expect-error untyped module
import { toArray, daysAgo } from 'UIComponents/core/SimpleDate';
import { DateFormats } from 'automation-ui-management/constants/DateConstants';
import { CUSTOM_PRESET_ID } from 'automation-ui-management/constants/DateRangePickerOptions';
import { RECENT_LOGS_FOR_ALERTING_DAY_LIMIT } from 'automation-ui-canvas/constants/PlatformHistoryDaysLimits';
import { HISTORY_ACTION_TYPES } from 'automation-ui-log-events/api-client/constants/HistoryActionTypes';
import { useQuery } from 'data-fetching-client';
import { GET_FLOW_HEALTH_INSIGHTS } from 'automation-ui-canvas/queries/insightsQueries';
import UIOptimisticNanoProgress from 'UIComponents/progress/UIOptimisticNanoProgress';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const TroubleShootIssuesPanel = ({
  onClose,
  flow
}) => {
  const isUngated = useGetIsUngated();
  const history = useHistory();
  const location = useLocation();
  const getTitle = getActionHeaderTitle({
    flowActions: flow.actions,
    firstActionId: flow.firstActionId,
    isUngated
  });
  const {
    data,
    loading
  } = useQuery(GET_FLOW_HEALTH_INSIGHTS, {
    variables: {
      flowId: flow.flowId
    }
  });
  const executionErrors = data ? data.flowHealthInsights.executionErrors : [];
  const enrollmentErrors = data ? data.flowHealthInsights.enrollmentErrors : [];
  const hasEnrollmentErrors = enrollmentErrors && enrollmentErrors.length > 0;
  return /*#__PURE__*/_jsxs(UIPanel, {
    width: 500,
    children: [/*#__PURE__*/_jsx(UIPanelHeader, {
      children: /*#__PURE__*/_jsxs(H2, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation.troubleshootIssues.title"
        }), /*#__PURE__*/_jsx(UICloseButton, {
          onClick: onClose
        })]
      })
    }), /*#__PURE__*/_jsxs(UIPanelBody, {
      children: [loading && /*#__PURE__*/_jsx(UIOptimisticNanoProgress, {}), /*#__PURE__*/_jsx(UIPanelSection, {
        children: !loading && !hasEnrollmentErrors && !executionErrors.length ? /*#__PURE__*/_jsxs(UIFlex, {
          direction: "column",
          align: "center",
          children: [/*#__PURE__*/_jsx(UIIllustration, {
            name: "success-green",
            width: "30%"
          }), /*#__PURE__*/_jsx(H4, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.troubleshootIssues.zeroState.title"
            })
          }), /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "automation.troubleshootIssues.zeroState.description_jsx",
            elements: {
              Link: UILink
            },
            options: {
              href: flowDetailsUrl(flow.flowId, basePaths.ACTION_LOGS, {
                [ApplicationHistoryFilterKeys.NOTIFICATION_TYPE]: NotificationTypes.ERROR,
                [ApplicationHistoryFilterKeys.RANGE_TYPE]: CUSTOM_PRESET_ID,
                [ApplicationHistoryFilterKeys.START_DATE]: I18n.moment(toArray(daysAgo(RECENT_LOGS_FOR_ALERTING_DAY_LIMIT))).format(DateFormats.DATE_QUERY_PARAM_FORMAT),
                [ApplicationHistoryFilterKeys.END_DATE]: I18n.moment().format(DateFormats.DATE_QUERY_PARAM_FORMAT)
              })
            }
          })]
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx("p", {
            className: "display-block",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.troubleshootIssues.description"
            })
          }), !loading && hasEnrollmentErrors && /*#__PURE__*/_jsx(UILink, {
            external: true,
            href: flowDetailsUrl(flow.flowId, basePaths.ACTION_LOGS, {
              [ApplicationHistoryFilterKeys.NOTIFICATION_TYPE]: NotificationTypes.ERROR,
              [ApplicationHistoryFilterKeys.ACTION_TYPE_ID]: HISTORY_ACTION_TYPES.ENROLLMENT,
              [ApplicationHistoryFilterKeys.RANGE_TYPE]: CUSTOM_PRESET_ID,
              [ApplicationHistoryFilterKeys.START_DATE]: I18n.moment(toArray(daysAgo(RECENT_LOGS_FOR_ALERTING_DAY_LIMIT))).format(DateFormats.DATE_QUERY_PARAM_FORMAT),
              [ApplicationHistoryFilterKeys.END_DATE]: I18n.moment().format(DateFormats.DATE_QUERY_PARAM_FORMAT)
            }),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.troubleshootIssues.enrollment"
            })
          }), !loading && executionErrors.length && executionErrors.map(action => ({
            title: getTitle({
              actionId: action.actionId,
              useString: true
            }),
            actionId: action.actionId
          })).sort((actionA, actionB) => actionA.title.localeCompare(actionB.title)).map(({
            title,
            actionId
          }) => {
            return /*#__PURE__*/_jsx(UILink, {
              onClick: () => {
                const actionType = flow.actions.get(actionId).actionType;
                const configRoute = actionType ? ActionConfigBasePaths[actionType] : undefined;
                const pathname = generatePath(PLATFORM_ACTION_EDIT, {
                  flowId: flow.flowId,
                  screen: 'edit',
                  actionId,
                  configRoute
                });
                history.replace({
                  pathname,
                  search: location.search
                });
              },
              className: "display-block",
              children: title
            }, actionId);
          })]
        })
      })]
    })]
  });
};